import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";

import { Professional } from "../../../../services/professionalService";
import {
  getSchedules,
  ProfessionalScheduleSlot,
  syncSchedules,
} from "../../../../services/professionalScheduleService";
import {
  applyBreakNameToAllDays,
  allPausesAlreadyHaveLabel,
  breakNamesWithDaysThatWouldChange,
  buildEmptyScheduleRows,
  countDaysWithPause,
  DayScheduleRow,
  rowsFromApi,
  rowsToSchedules,
  validateScheduleRows,
  withPreservedWindowTime,
  withPreservedWorkLabel,
} from "./professionalScheduleRows";

type ScheduleApiError = {
  data?: string;
  code?: string;
  details?: Record<string, string[]>;
};

const extractFirstValidationMessage = (error: ScheduleApiError): string | null => {
  const details = error.details;

  if (!details) {
    return null;
  }

  for (const messages of Object.values(details)) {
    if (Array.isArray(messages) && messages.length > 0 && messages[0]) {
      return messages[0];
    }
  }

  return null;
};

const resolveSaveErrorMessage = (
  error: ScheduleApiError,
  fallback: string
): string => {
  if (error.code === "VALIDATION_ERROR") {
    const firstDetail = extractFirstValidationMessage(error);

    if (firstDetail) {
      return firstDetail;
    }
  }

  if (error.data) {
    return error.data;
  }

  return fallback;
};

interface ProfessionalScheduleModalProps {
  professional: Professional | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (schedules: ProfessionalScheduleSlot[]) => void;
}

const ProfessionalScheduleModal = ({
  professional,
  isOpen,
  onClose,
  onSaved,
}: ProfessionalScheduleModalProps) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<DayScheduleRow[]>(buildEmptyScheduleRows);
  const [weekBreakName, setWeekBreakName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showUnitRequiredBanner, setShowUnitRequiredBanner] = useState(false);

  const dayLabels = useMemo(
    () =>
      Array.from({ length: 7 }, (_, dayOfWeek) =>
        t(`professionals.schedule.days.${dayOfWeek}`)
      ),
    [t]
  );

  const loadRows = useCallback(async () => {
    if (!professional) {
      return;
    }

    setLoading(true);

    try {
      const response = await getSchedules(professional.id);
      setRows(rowsFromApi(response.data));
    } catch (error: unknown) {
      const apiError = error as ScheduleApiError;
      toast.error(apiError?.data || t("professionals.schedule.toast.loadError"));
      setRows(buildEmptyScheduleRows());
    } finally {
      setLoading(false);
    }
  }, [professional, t]);

  useEffect(() => {
    if (!isOpen) {
      setShowUnitRequiredBanner(false);
      setWeekBreakName("");
      return;
    }

    if (!professional) {
      return;
    }

    setShowUnitRequiredBanner(false);
    loadRows();
  }, [isOpen, professional, loadRows]);

  const updateRow = (dayOfWeek: number, patch: Partial<DayScheduleRow>) => {
    setShowUnitRequiredBanner(false);
    setRows((previous) =>
      previous.map((row) =>
        row.day_of_week === dayOfWeek ? { ...row, ...patch } : row
      )
    );
  };

  const updatePreservedBreakLabel = (
    dayOfWeek: number,
    windowIndex: number,
    label: string
  ) => {
    setRows((previous) =>
      previous.map((row) => {
        if (row.day_of_week !== dayOfWeek || !row.preservedWindows) {
          return row;
        }

        return {
          ...row,
          preservedWindows: row.preservedWindows.map((window, index) =>
            index === windowIndex
              ? { ...window, break_label: label.slice(0, 40) }
              : window
          ),
        };
      })
    );
  };

  const pauseDayCount = countDaysWithPause(rows);

  const applyNameToEveryPause = async (label: string) => {
    const trimmed = label.trim().slice(0, 40);
    const count = countDaysWithPause(rows);

    if (trimmed === "" || count === 0) {
      toast.info(t("professionals.schedule.break.applyNone"));
      return;
    }

    if (allPausesAlreadyHaveLabel(rows, trimmed)) {
      toast.info(
        count === 1
          ? t("professionals.schedule.break.alreadyAppliedOne", { label: trimmed })
          : t("professionals.schedule.break.alreadyApplied", { label: trimmed, count })
      );
      return;
    }

    const previousWithDays = breakNamesWithDaysThatWouldChange(rows, trimmed);

    if (previousWithDays.length > 0) {
      const namesWithDays = previousWithDays
        .map((entry) => {
          const days = entry.days
            .map((day) => t(`professionals.schedule.days.${day}`))
            .join(", ");
          return t("professionals.schedule.break.replaceNameOnDays", {
            name: entry.name,
            days,
          });
        })
        .join("; ");

      const confirmation = await Swal.fire({
        title: t("professionals.schedule.break.replaceTitle"),
        text: t("professionals.schedule.break.replaceTextWithDays", {
          details: namesWithDays,
          label: trimmed,
        }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("professionals.schedule.break.replaceConfirm"),
        cancelButtonText: t("professionals.schedule.break.replaceCancel"),
        cancelButtonColor: "#878a99",
        focusCancel: true,
      });

      if (!confirmation.isConfirmed) {
        return;
      }
    }

    setRows((previous) => applyBreakNameToAllDays(previous, trimmed));
    toast.success(
      previousWithDays.length > 0
        ? count === 1
          ? t("professionals.schedule.break.appliedReplacedOne", { label: trimmed })
          : t("professionals.schedule.break.appliedReplaced", { label: trimmed, count })
        : count === 1
          ? t("professionals.schedule.break.appliedOne", { label: trimmed })
          : t("professionals.schedule.break.applied", { label: trimmed, count })
    );
  };

  const setPreservedWindowTime = (
    dayOfWeek: number,
    index: number,
    edge: "start_time" | "end_time",
    value: string
  ) => {
    setShowUnitRequiredBanner(false);
    setRows((previous) =>
      previous.map((row) =>
        row.day_of_week === dayOfWeek
          ? withPreservedWindowTime(row, index, edge, value)
          : row
      )
    );
  };

  const applyNameLabel =
    pauseDayCount === 1
      ? t("professionals.schedule.break.applyAllOne")
      : pauseDayCount > 1
        ? t("professionals.schedule.break.applyAllCounted", { count: pauseDayCount })
        : t("professionals.schedule.break.applyAll");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!professional) {
      return;
    }

    const validationError = validateScheduleRows(rows);

    if (validationError) {
      toast.error(t(`professionals.schedule.validation.${validationError}`));
      return;
    }

    const schedules = rowsToSchedules(rows);

    setSubmitting(true);
    setShowUnitRequiredBanner(false);

    try {
      const response = await syncSchedules(professional.id, { schedules });
      toast.success(response.message || t("professionals.schedule.toast.saveSuccess"));
      setRows(rowsFromApi(response.data));
      onSaved?.(response.data);
      onClose();
    } catch (error: unknown) {
      const apiError = error as ScheduleApiError;

      if (apiError.code === "UNIT_SCHEDULE_REQUIRED") {
        setShowUnitRequiredBanner(true);
        return;
      }

      toast.error(
        resolveSaveErrorMessage(apiError, t("professionals.schedule.toast.saveError"))
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered className="professional-schedule-modal">
      <ModalHeader toggle={onClose}>
        {professional
          ? t("professionals.schedule.title", { name: professional.name })
          : t("professionals.schedule.titleFallback")}
      </ModalHeader>
      <Form onSubmit={handleSubmit} className="professional-schedule-modal__form d-flex flex-column">
        <ModalBody className="professional-schedule-modal__body overflow-auto">
          <p className="text-muted">{t("professionals.schedule.description")}</p>

          {showUnitRequiredBanner ? (
            <Alert color="warning" className="border-0 bg-warning-subtle text-warning">
              <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
                <span>{t("professionals.schedule.unit_required_banner")}</span>
                <Link
                  to="/units"
                  className="btn btn-sm btn-warning flex-shrink-0"
                  onClick={onClose}
                >
                  {t("professionals.schedule.unit_required_action")}
                </Link>
              </div>
            </Alert>
          ) : null}

          {!loading ? (
            <div className="mb-3">
              <label className="form-label" htmlFor="schedule-break-name-week">
                {t("professionals.schedule.break.weekName")}
              </label>
              <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2">
                <Input
                  id="schedule-break-name-week"
                  type="text"
                  bsSize="sm"
                  className="flex-grow-1"
                  style={{ maxWidth: "16rem" }}
                  maxLength={40}
                  value={weekBreakName}
                  placeholder={t("professionals.schedule.break.namePlaceholder")}
                  onChange={(event) => setWeekBreakName(event.target.value.slice(0, 40))}
                />
                {weekBreakName.trim() ? (
                  <Button
                    type="button"
                    color="link"
                    size="sm"
                    className="p-0 text-start"
                    onClick={() => applyNameToEveryPause(weekBreakName)}
                  >
                    {applyNameLabel}
                  </Button>
                ) : null}
              </div>
              <p className="text-muted small mb-0 mt-1">
                {t("professionals.schedule.break.weekHint")}
              </p>
            </div>
          ) : null}

          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary">{t("professionals.loading")}</Spinner>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "8rem" }}>{t("professionals.schedule.table.day")}</th>
                    <th style={{ width: "6rem" }}>{t("professionals.schedule.table.active")}</th>
                    <th>{t("professionals.schedule.table.start")}</th>
                    <th>{t("professionals.schedule.table.end")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <Fragment key={row.day_of_week}>
                      <tr>
                        <td className="fw-medium">{dayLabels[row.day_of_week]}</td>
                        <td>
                          <Input
                            type="checkbox"
                            checked={row.enabled}
                            onChange={(event) =>
                              updateRow(
                                row.day_of_week,
                                event.target.checked
                                  ? {
                                      enabled: true,
                                      preservedWindows: null,
                                    }
                                  : { enabled: false }
                              )
                            }
                            aria-label={t("professionals.schedule.table.activeFor", {
                              day: dayLabels[row.day_of_week],
                            })}
                          />
                        </td>
                        <td>
                          <Input
                            type="time"
                            value={row.start_time}
                            disabled={!row.enabled}
                            onChange={(event) => {
                              if (row.preservedWindows) {
                                setPreservedWindowTime(
                                  row.day_of_week,
                                  0,
                                  "start_time",
                                  event.target.value
                                );
                                return;
                              }

                              updateRow(row.day_of_week, { start_time: event.target.value });
                            }}
                          />
                        </td>
                        <td>
                          <Input
                            type="time"
                            value={row.end_time}
                            disabled={!row.enabled}
                            onChange={(event) => {
                              if (row.preservedWindows) {
                                setPreservedWindowTime(
                                  row.day_of_week,
                                  row.preservedWindows.length - 1,
                                  "end_time",
                                  event.target.value
                                );
                                return;
                              }

                              updateRow(row.day_of_week, { end_time: event.target.value });
                            }}
                          />
                        </td>
                      </tr>
                      {row.enabled && row.preservedWindows ? (
                        <tr>
                          <td />
                          <td colSpan={3}>
                            <p className="text-muted small mb-2">
                              {t("professionals.schedule.break.several")}
                            </p>
                            <div className="d-flex flex-column gap-2">
                              {row.preservedWindows.map((window, index, windows) => {
                                const next = windows[index + 1];

                                if (!next) {
                                  return null;
                                }

                                const pauseLabel = window.break_label ?? "";
                                const hasGap = next.start_time > window.end_time;
                                const middle = index > 0 ? windows[index] : null;

                                return (
                                  <div
                                    key={`${row.day_of_week}-pause-${index}`}
                                    className="d-flex flex-column gap-1"
                                  >
                                    {middle ? (
                                      <div className="d-flex flex-column gap-1 mb-1">
                                        <p className="small fw-medium mb-0">
                                          {t("professionals.schedule.break.middleStretch", {
                                            start: middle.start_time,
                                            end: middle.end_time,
                                          })}
                                        </p>
                                        <label
                                          className="small mb-0"
                                          htmlFor={`schedule-work-name-${row.day_of_week}-${index}`}
                                        >
                                          {t("professionals.schedule.break.workNameVisible")}
                                        </label>
                                        <Input
                                          id={`schedule-work-name-${row.day_of_week}-${index}`}
                                          type="text"
                                          bsSize="sm"
                                          className="flex-grow-1"
                                          style={{ minWidth: "12rem", maxWidth: "16rem" }}
                                          maxLength={40}
                                          value={middle.work_label ?? ""}
                                          placeholder={t(
                                            "professionals.schedule.break.workNamePlaceholder"
                                          )}
                                          onChange={(event) => {
                                            const nextLabel = event.target.value;
                                            setRows((previous) =>
                                              previous.map((item) =>
                                                item.day_of_week === row.day_of_week
                                                  ? withPreservedWorkLabel(
                                                      item,
                                                      index,
                                                      nextLabel
                                                    )
                                                  : item
                                              )
                                            );
                                          }}
                                        />                                      </div>
                                    ) : null}
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                      <Input
                                        type="time"
                                        bsSize="sm"
                                        className="w-auto"
                                        value={window.end_time}
                                        aria-label={t("professionals.schedule.break.start")}
                                        onChange={(event) =>
                                          setPreservedWindowTime(
                                            row.day_of_week,
                                            index,
                                            "end_time",
                                            event.target.value
                                          )
                                        }
                                      />
                                      <span className="small text-muted">
                                        {t("professionals.schedule.break.until")}
                                      </span>
                                      <Input
                                        type="time"
                                        bsSize="sm"
                                        className="w-auto"
                                        value={next.start_time}
                                        aria-label={t("professionals.schedule.break.end")}
                                        onChange={(event) =>
                                          setPreservedWindowTime(
                                            row.day_of_week,
                                            index + 1,
                                            "start_time",
                                            event.target.value
                                          )
                                        }
                                      />
                                    </div>
                                    {hasGap ? (
                                      <>
                                        <label
                                          className="small mb-0"
                                          htmlFor={`schedule-break-name-${row.day_of_week}-${index}`}
                                        >
                                          {t("professionals.schedule.break.nameVisible")}
                                        </label>
                                        <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2">
                                          <Input
                                            id={`schedule-break-name-${row.day_of_week}-${index}`}
                                            type="text"
                                            bsSize="sm"
                                            className="flex-grow-1"
                                            style={{ minWidth: "12rem", maxWidth: "16rem" }}
                                            maxLength={40}
                                            value={pauseLabel}
                                            placeholder={t("professionals.schedule.break.namePlaceholder")}
                                            onChange={(event) =>
                                              updatePreservedBreakLabel(
                                                row.day_of_week,
                                                index,
                                                event.target.value
                                              )
                                            }
                                          />
                                          {pauseLabel.trim() ? (
                                            <Button
                                              type="button"
                                              color="link"
                                              size="sm"
                                              className="p-0 text-start"
                                              onClick={() => applyNameToEveryPause(pauseLabel)}
                                            >
                                              {applyNameLabel}
                                            </Button>
                                          ) : null}
                                        </div>
                                      </>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                      {row.enabled && !row.preservedWindows ? (
                        <tr>
                          <td />
                          <td colSpan={3}>
                            <div className="d-flex flex-column gap-2">
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                <Input
                                  type="checkbox"
                                  className="form-check-input m-0"
                                  checked={row.break_enabled}
                                  id={`schedule-break-${row.day_of_week}`}
                                  onChange={(event) =>
                                    updateRow(row.day_of_week, {
                                      break_enabled: event.target.checked,
                                    })
                                  }
                                  aria-label={t("professionals.schedule.break.activeFor", {
                                    day: dayLabels[row.day_of_week],
                                  })}
                                />
                                <label
                                  htmlFor={`schedule-break-${row.day_of_week}`}
                                  className="mb-0 small"
                                >
                                  {t("professionals.schedule.break.label")}
                                </label>
                                <Input
                                  type="time"
                                  bsSize="sm"
                                  className="w-auto"
                                  value={row.break_start}
                                  disabled={!row.break_enabled}
                                  aria-label={t("professionals.schedule.break.start")}
                                  onChange={(event) =>
                                    updateRow(row.day_of_week, { break_start: event.target.value })
                                  }
                                />
                                <span className="small text-muted">
                                  {t("professionals.schedule.break.until")}
                                </span>
                                <Input
                                  type="time"
                                  bsSize="sm"
                                  className="w-auto"
                                  value={row.break_end}
                                  disabled={!row.break_enabled}
                                  aria-label={t("professionals.schedule.break.end")}
                                  onChange={(event) =>
                                    updateRow(row.day_of_week, { break_end: event.target.value })
                                  }
                                />
                              </div>
                              {row.break_enabled ? (
                                <div className="d-flex flex-column gap-1">
                                  <label
                                    className="small mb-0"
                                    htmlFor={`schedule-break-name-${row.day_of_week}`}
                                  >
                                    {t("professionals.schedule.break.nameVisible")}
                                  </label>
                                  <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2">
                                    <Input
                                      id={`schedule-break-name-${row.day_of_week}`}
                                      type="text"
                                      bsSize="sm"
                                      className="flex-grow-1"
                                      style={{ minWidth: "12rem", maxWidth: "16rem" }}
                                      maxLength={40}
                                      value={row.break_label}
                                      placeholder={t("professionals.schedule.break.namePlaceholder")}
                                      aria-label={t("professionals.schedule.break.name")}
                                      onChange={(event) =>
                                        updateRow(row.day_of_week, {
                                          break_label: event.target.value.slice(0, 40),
                                        })
                                      }
                                    />
                                    {row.break_label.trim() ? (
                                      <Button
                                        type="button"
                                        color="link"
                                        size="sm"
                                        className="p-0 text-start"
                                        onClick={() => applyNameToEveryPause(row.break_label)}
                                      >
                                        {applyNameLabel}
                                      </Button>
                                    ) : null}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="professional-schedule-modal__footer flex-shrink-0 border-top bg-body">
          <Button color="light" type="button" onClick={onClose} disabled={submitting}>
            {t("professionals.actions.cancel")}
          </Button>
          <Button color="primary" type="submit" disabled={loading || submitting}>
            {submitting ? (
              <Spinner size="sm" className="me-1" />
            ) : (
              <i className="ri-save-line align-bottom me-1" />
            )}
            {t("professionals.schedule.actions.save")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ProfessionalScheduleModal;
